import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable, Subscription } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { HttpService } from '@app/services/http.service';
import { DomSanitizer } from '@angular/platform-browser';
import { Patient } from '@app/models/patient';
import { environment } from '@env/environment';
import { LoaderService } from '@app/services/loader.service';
import { BaseComponent } from '@app/base.component';
import { ImageCompressor } from '@app/services/image-compressor.service';
import { MatDialog } from '@angular/material/dialog';
import { SnackbarService } from '@app/services/snackbar.service';
import { ChartUploadService } from '@app/services/chartUpload.service';

@Component({
  selector: 'app-patient-chart',
  templateUrl: './patient-chart.component.html',
  styleUrls: ['./patient-chart.component.css']
})
export class PatientChartComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {

  readonly zoomStepPercent: number = 25;
  readonly zoomMinPercent: number = 1;
  readonly zoomBasePercent: number = 100;

  @Input('patient') patient: Patient = null;
  @Input('hasButtonUpload') hasButtonUpload: Boolean = false;
  @Input('hasNewPatient') hasNewPatient: Boolean = false;
  @Output('onButtonBack') onButtonBack: EventEmitter<any> = new EventEmitter<any>();
  @Output('onButtonUpload') onButtonUpload: EventEmitter<any> = new EventEmitter<any>();
  @Output('onButtonSkip') onButtonSkip: EventEmitter<any> = new EventEmitter<any>();
  @Output('onChartDelete') onChartDelete: EventEmitter<any> = new EventEmitter<any>();

  chartSubscription: Subscription;
  chartImage: any = null;
  chartImageDownloadUrl: string = null;
  isChartLoaded: boolean = false;
  areZoomControlsVisible: boolean = true;
  private hasUserSetZoomControlsVisibility: boolean = false;
  zoomDefaultPercent: number = this.zoomBasePercent;
  zoomPercent: number = this.zoomBasePercent;

  constructor(
    public dialogDelete: MatDialog,
    private httpService: HttpService,
    private loaderService: LoaderService,
    private domSanitizer: DomSanitizer,
    private imageCompressor: ImageCompressor,
    private snackbarService: SnackbarService,
    private chartUploadService: ChartUploadService,
    private breakpointObserver: BreakpointObserver
  ) { 
    super();
  }

  // initialize component
  ngOnInit(): void {
    this.breakpointObserver.observe(Breakpoints.Handset).pipe(
      takeUntil(this.appUnsubscribe)
    ).subscribe(result => {
      if (!this.hasUserSetZoomControlsVisibility) {
        this.areZoomControlsVisible = !result.matches;
      }
    });

    this.chartUploadService.chartUploaded$.pipe(
      takeUntil(this.appUnsubscribe)
    ).subscribe(patientId => {
      if (this.patient && this.patient.patient_id === patientId) {
        this.refreshChart();
      }
    });
  }

  // check for input changes
  ngOnChanges( changes: SimpleChanges ): void {
    if( changes.patient.currentValue && (!changes.patient.previousValue || changes.patient.currentValue.patient_id !== changes.patient.previousValue.patient_id) ) {
      this.clearChartImageUrl();
      this.initializeZoom();
      this.refreshChart();
    }
  }

  // fetch patient charts
  private fetchChart(): Observable<any> {
    const urlApiChart = this.httpService.createUrl([
      'api',
      'patients',
      this.patient.patient_id,
      'chart'
    ], {}, {
      host: environment.host_api_default
    });

    let chartObservable = this.httpService.get<any>( urlApiChart, {
      responseType: 'blob'
    }).pipe(
      tap( _ => null ),
      map(( result ) => {
        if( result.type === 'application/json' ) {
          // api returned json instead of image blob, this happens when the image is missing or some other error occurs

          // check for response other than 200
          this.clearChartImageUrl();
          this.chartImage = null;
        } else {
          this.clearChartImageUrl();
          const blob = new Blob([result], { type: 'image/jpeg' });
          this.chartImageDownloadUrl = window.URL.createObjectURL( blob);
          this.chartImage = this.domSanitizer.bypassSecurityTrustResourceUrl( this.chartImageDownloadUrl );
          this.initializeZoom();
        }
        this.isChartLoaded = true;
        return result;
      })
    );
    return chartObservable;
  }

  private refreshChart(): void {
    this.chartSubscription = this.fetchChart().pipe(
      takeUntil(this.appUnsubscribe)
    ).subscribe(_ => {
      // chartImage is updated inside fetchChart
    });
  }

  handleSkipStep(): void {
    this.onButtonSkip.emit();
  }

  handleZoomOut(): void {
    this.zoomPercent = Math.max(this.zoomMinPercent, this.zoomPercent - this.zoomStepPercent);
  }

  handleZoomReset(): void {
    this.zoomPercent = this.zoomDefaultPercent;
  }

  handleZoomIn(): void {
    this.zoomPercent = this.zoomPercent + this.zoomStepPercent;
  }

  handleToggleZoomControls(): void {
    this.hasUserSetZoomControlsVisibility = true;
    this.areZoomControlsVisible = !this.areZoomControlsVisible;
  }

  handleButtonDownloadChart(): void {
    if (!this.chartImageDownloadUrl || !this.patient) {
      return;
    }

    const link = document.createElement('a');
    link.href = this.chartImageDownloadUrl;
    link.download = `patient_chart_${this.patient.patient_id}.jpg`;
    link.click();
  }

  // when delete button is clicked
  handleButtonDeleteChart(): void {
    const dialogReference = this.dialogDelete.open( PatientChartDialogDeleteComponent );
    dialogReference.afterClosed()
    .pipe( takeUntil( this.appUnsubscribe ) )
    .subscribe({
      next: ( r ) => {
        if( r )
        {
          this.handleDeleteChart();
        }
      }
    }); 
  }

  handleDeleteChart(): void {
    const urlApiChart = this.httpService.createUrl([
      'api',
      'patients',
      this.patient.patient_id,
      'chart'
    ], {}, {
      host: environment.host_api_default
    });

    this.loaderService.show();
    let deleteRequest = this.httpService.delete( urlApiChart );
    deleteRequest.subscribe( result => {
      this.clearChartImageUrl();
      this.chartImage = null;
      this.isChartLoaded = true;
      this.initializeZoom();
      this.loaderService.hide();
      this.onChartDelete.emit();
    }, error => {
      this.loaderService.hide();
      this.snackbarService.show(`Error deleting chart: ${error.error.message || error.statusText}`);
    });
  }

  // upload chart cloud storage
  // https://stackoverflow.com/questions/48746467/upload-image-to-web-api-2-using-angular-5
  async handleAddChart( event: any ): Promise<void> {
    
    let imageTypes = [
      'image/jpeg',
      'image/gif',
      'image/png',
      'image/webp'
    ];

    // get files from file input
    let files: FileList = event.target.files;
    let file = files.item(0);
    let imageFile: string = '';
    let imageOrientation: any = null;

    if( !imageTypes.includes( file.type ) )
    {
      this.snackbarService.show('Error: You may only upload image files.');
      return;
    }

    try {
      this.loaderService.show();

      // start processing image
      let image = await this.imageCompressor.uploadFile( event ).then(({ image, orientation }) => {
        imageFile = image;
        imageOrientation = orientation;
        return this.imageCompressor.imageData( imageFile );
      });

      // find out the size of of the image, we need to shrink it down to a 
      // file that's no greater than 2400px tall or wide
      let compressionScale: number = 100;
      let maxWidthHeight: number = 2400;
      let maxValue: number = Math.max( image.naturalWidth, image.naturalWidth );

      // if the width or height is greater than maxWidthHeight, set the scale
      // to reduce it down to the maximum allowed
      if( maxValue > maxWidthHeight ){
        compressionScale = parseFloat(Math.round((maxWidthHeight/maxValue) * 100).toFixed(2));
      }
        
      // compress image file
      image = await this.imageCompressor.compressFile( imageFile, imageOrientation, compressionScale, "image/jpeg", 75);

      // upload image to remote server
      try {
        await this.chartUploadService.uploadChart(this.patient.patient_id, image);
        this.loaderService.hide();
        this.onButtonUpload.emit();
      } catch (x) {
        this.loaderService.hide();
        const errorCode = x instanceof Error ? x.message : x;
        if (errorCode === 'existing') {
          this.snackbarService.show("Already trying to upload a chart for this patient");
        } else if (errorCode === 'timeout') {
          this.snackbarService.show("Upload timed out on a slow connection. The image was saved in your browser. As soon as the connection is restored, the app will retry in the background.");
        } else if (errorCode === 'not-authenticated') {
          this.snackbarService.show("Chart upload could not start because this session is missing the encryption key needed for background uploads. If you're using bypass login, use a normal login for chart uploads.");
        } else {
          this.snackbarService.show("Error uploading image - Likely due to network issues. The image was saved in your browser. The app will upload it in the background once the connection is restored.");
        }
      }
  
    } catch {
      this.loaderService.hide();
      this.snackbarService.show('Error processing image file. Please try again.');
    } finally {
      // what to do when promise ends
    };
  }

  /**
   * Generic tooltip function.
   * 
   * @param toolTip 
   */
  openSnackBar(toolTip: string) {
    this.snackbarService.show(toolTip);
  }

  handleFitZoomCalculated(fitZoomPercent: number): void {
    const normalizedFitZoom = Math.max(this.zoomMinPercent, Math.round(fitZoomPercent));
    const shouldApplyCurrentZoom = this.zoomPercent === this.zoomDefaultPercent || !this.chartImage;
    this.zoomDefaultPercent = normalizedFitZoom;

    if (shouldApplyCurrentZoom) {
      this.zoomPercent = normalizedFitZoom;
    }
  }

  handleZoomPercentChange(zoomPercent: number): void {
    this.zoomPercent = Math.max(this.zoomMinPercent, Math.round(zoomPercent));
  }

  ngOnDestroy(): void {
    this.clearChartImageUrl();
    if (this.chartSubscription) {
      this.chartSubscription.unsubscribe();
    }
    super.ngOnDestroy();
  }

  private clearChartImageUrl(): void {
    if (this.chartImageDownloadUrl) {
      window.URL.revokeObjectURL(this.chartImageDownloadUrl);
      this.chartImageDownloadUrl = null;
    }
  }

  private initializeZoom(): void {
    this.zoomDefaultPercent = this.zoomBasePercent;
    this.zoomPercent = this.zoomBasePercent;
  }

}


// dialog specific to this component
@Component({
  selector: 'app-patient-chart-dialog-delete',
  templateUrl: 'patient-chart-dialog-delete.html'
})
export class PatientChartDialogDeleteComponent {
}
